/**
 * Cognito 用户信息获取工具
 * 用于从 ID Token 中解析用户属性
 */

class CognitoUserInfo {
  constructor() {
    this.idToken = null;
    this.userAttributes = null;
  }

  /**
   * 从 localStorage 获取 ID Token
   * @returns {string|null} ID Token 字符串
   */
  getIdToken() {
    this.idToken = localStorage.getItem('idToken');
    return this.idToken;
  }

  /**
   * 解码 JWT Token 的 payload 部分
   * @param {string} token - JWT Token
   * @returns {object|null} 解码后的 payload 对象
   */
  decodeToken(token) {
    if (!token) return null;

    try {
      // JWT 格式: header.payload.signature
      const parts = token.split('.');
      if (parts.length !== 3) {
        console.error('Invalid token format');
        return null;
      }

      // 获取 payload 部分（第二部分）
      const payload = parts[1];

      // Base64 URL 解码
      // 替换 URL 安全字符并添加填充
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      const paddedBase64 = base64.padEnd(base64.length + (4 - base64.length % 4) % 4, '=');

      // 解码 Base64
      const jsonPayload = decodeURIComponent(
        atob(paddedBase64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );

      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  }

  /**
   * 获取用户的所有属性
   * @returns {object|null} 用户属性对象
   */
  getUserAttributes() {
    const token = this.getIdToken();
    if (!token) {
      console.warn('No ID Token found in localStorage');
      return null;
    }

    this.userAttributes = this.decodeToken(token);
    return this.userAttributes;
  }

  /**
   * 获取用户的 region 信息
   * @returns {string|null} 地区代码（如 'US', 'CA', 'DE'）
   */
  getRegion() {
    const attributes = this.getUserAttributes();
    if (!attributes) return null;

    // Cognito 自定义属性通常以 'custom:' 为前缀
    return attributes['custom:region'] || attributes['region'] || 'US';
  }

  /**
   * 获取用户的邮箱
   * @returns {string|null}
   */
  getEmail() {
    const attributes = this.getUserAttributes();
    return attributes ? attributes.email : null;
  }

  /**
   * 获取用户的名字
   * @returns {string|null}
   */
  getFirstName() {
    const attributes = this.getUserAttributes();
    return attributes ? (attributes.given_name || attributes['custom:firstName']) : null;
  }

  /**
   * 获取用户的姓氏
   * @returns {string|null}
   */
  getLastName() {
    const attributes = this.getUserAttributes();
    return attributes ? (attributes.family_name || attributes['custom:lastName']) : null;
  }

  /**
   * 获取用户的完整姓名
   * @returns {string|null}
   */
  getFullName() {
    const firstName = this.getFirstName();
    const lastName = this.getLastName();

    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    }

    return firstName || lastName || null;
  }

  /**
   * 清除用户信息和 tokens
   */
  clearUserInfo() {
    localStorage.removeItem('idToken');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('cognito_authenticated');
    this.idToken = null;
    this.userAttributes = null;

    // 触发自定义事件，通知其他组件用户已登出
    console.log('[Cognito] 触发 user:logout 事件');
    window.dispatchEvent(new CustomEvent('user:logout'));
  }

  /**
   * 统一的登出管理函数
   * 清除所有 Cognito 状态并跳转到 Shopify 登出页面
   * @param {String} returnTo - 登出后跳转的路径（默认 '/account/login'）
   */
  handleLogout(returnTo = '/account/login') {
    console.log('[Cognito] 执行统一登出流程');

    // 1. 清除 Cognito tokens 和用户信息
    this.clearUserInfo();

    // 2. 延迟一小段时间，确保事件监听器能处理完
    setTimeout(() => {
      // 3. 跳转到 Shopify 登出 URL
      const logoutUrl = '/account/logout';
      window.location.href = `${logoutUrl}?return_to=${returnTo}`;
    }, 100);
  }

  /**
   * 打印所有用户属性到控制台（用于调试）
   */
  debugPrintAttributes() {
    const attributes = this.getUserAttributes();
    console.log('=== Cognito User Attributes ===');
    console.log(JSON.stringify(attributes, null, 2));
    console.log('===============================');
  }
}

// 创建全局实例
window.cognitoUserInfo = new CognitoUserInfo();

// 使用示例：
//
// // 获取用户的 region
// const region = window.cognitoUserInfo.getRegion();
// console.log('User region:', region);
//
// // 获取用户的邮箱
// const email = window.cognitoUserInfo.getEmail();
// console.log('User email:', email);
//
// // 获取用户的完整姓名
// const fullName = window.cognitoUserInfo.getFullName();
// console.log('User name:', fullName);
//
// // 获取所有用户属性
// const allAttributes = window.cognitoUserInfo.getUserAttributes();
// console.log('All user attributes:', allAttributes);
//
// // 调试：打印所有属性
// window.cognitoUserInfo.debugPrintAttributes();
